import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Header,
  Icon,
  Label,
  Loader,
  Message,
  Table,
} from 'semantic-ui-react';

import { API, showError, showSuccess } from '../../helpers';

const formatPeriod = (invoice) => {
  const date = new Date(Number(invoice.period_start) * 1000);
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
};

const formatDateTime = (timestamp) => {
  if (!timestamp) return '-';
  return new Date(Number(timestamp) * 1000).toLocaleString();
};

const MonthlyDataSection = ({ title, empty, children }) => (
  <section className='billing-data-section-v8'>
    <Header as='h3'>{title}</Header>
    {empty ? <Message info content={empty} /> : children}
  </section>
);
const InvoiceStatus = ({ invoice, t }) => (
  <Label color={invoice.payment_status === 'paid' ? 'green' : 'red'} basic>
    {t('billing.monthly.status.' + invoice.payment_status)}
  </Label>
);

export const MonthlyInvoiceTableV98 = ({
  invoices,
  admin = false,
  onPayment,
}) => {
  const { t } = useTranslation();
  return (
    <div className='management-table-scroll-v72' role='region' tabIndex={0}>
      <Table basic='very' compact unstackable className='management-table-v72'>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>{t('billing.monthly.period')}</Table.HeaderCell>
            <Table.HeaderCell>{t('billing.monthly.amount')}</Table.HeaderCell>
            <Table.HeaderCell>{t('billing.monthly.mode')}</Table.HeaderCell>
            <Table.HeaderCell>{t('billing.monthly.payment')}</Table.HeaderCell>
            {admin ? (
              <Table.HeaderCell>{t('billing.monthly.action')}</Table.HeaderCell>
            ) : null}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {invoices.map((invoice) => {
            const paid = invoice.payment_status === 'paid';
            return (
              <Table.Row key={invoice.period_start}>
                <Table.Cell>
                  <strong>{formatPeriod(invoice)}</strong>
                  {invoice.current ? (
                    <Label
                      basic
                      color='blue'
                      size='tiny'
                      className='billing-monthly-current-v98'
                    >
                      {t('billing.monthly.current')}
                    </Label>
                  ) : null}
                  <span className='billing-cell-secondary-v8'>
                    {new Date(
                      Number(invoice.period_start) * 1000
                    ).toLocaleDateString()}{' '}
                    -{' '}
                    {new Date(
                      Number(invoice.period_end) * 1000
                    ).toLocaleDateString()}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <strong>USD {invoice.amount_usd}</strong>
                </Table.Cell>
                <Table.Cell>
                  {t('billing.mode.' + invoice.payment_mode)}
                </Table.Cell>
                <Table.Cell>
                  <InvoiceStatus invoice={invoice} t={t} />
                  {invoice.paid_at ? (
                    <span className='billing-cell-secondary-v8'>
                      {formatDateTime(invoice.paid_at)}
                    </span>
                  ) : null}
                </Table.Cell>
                {admin ? (
                  <Table.Cell>
                    <Button
                      basic
                      size='tiny'
                      color={paid ? undefined : 'green'}
                      icon
                      labelPosition='left'
                      onClick={() => onPayment(invoice)}
                    >
                      <Icon name={paid ? 'undo' : 'check'} />
                      {paid
                        ? t('billing.monthly.mark_unpaid')
                        : t('billing.monthly.mark_paid')}
                    </Button>
                  </Table.Cell>
                ) : null}
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </div>
  );
};

export const CustomerMonthlyInvoicesV98 = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await API.get('/api/billing/v8/self/monthly-invoices');
      if (!response) return;
      if (!response.data?.success) throw new Error(response.data?.message);
      setData(response.data.data || {});
    } catch (error) {
      showError(error.message || t('billing.monthly.errors.load'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <Loader active inline='centered' />;
  if (!data?.eligible) return null;
  const invoices = data.invoices || [];
  return (
    <MonthlyDataSection
      title={t('billing.monthly.title')}
      empty={invoices.length ? '' : t('billing.monthly.empty')}
    >
      <div className='billing-monthly-subtitle-v98'>
        {t('billing.monthly.subtitle')}
      </div>
      {invoices.length ? <MonthlyInvoiceTableV98 invoices={invoices} /> : null}
    </MonthlyDataSection>
  );
};

export const RootMonthlyInvoicesV98 = () => {
  const { t } = useTranslation();
  const [organizations, setOrganizations] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadOrganizations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await API.get(
        '/api/billing/v8/admin/monthly-invoices/organizations'
      );
      if (!response) return;
      if (!response.data?.success) throw new Error(response.data?.message);
      const rows = response.data.data?.organizations || [];
      setOrganizations(rows);
      setSelectedAccount((current) =>
        rows.some((row) => String(row.account_id) === current)
          ? current
          : rows.length
          ? String(rows[0].account_id)
          : ''
      );
    } catch (error) {
      showError(error.message || t('billing.monthly.errors.load'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadHistory = useCallback(async () => {
    if (!selectedAccount) {
      setInvoices([]);
      return;
    }
    setHistoryLoading(true);
    try {
      const response = await API.get(
        '/api/billing/v8/admin/accounts/' +
          selectedAccount +
          '/monthly-invoices'
      );
      if (!response) return;
      if (!response.data?.success) throw new Error(response.data?.message);
      setInvoices(response.data.data?.invoices || []);
    } catch (error) {
      showError(error.message || t('billing.monthly.errors.load'));
    } finally {
      setHistoryLoading(false);
    }
  }, [selectedAccount, t]);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const selectedOrganization = useMemo(
    () =>
      organizations.find((row) => String(row.account_id) === selectedAccount),
    [organizations, selectedAccount]
  );

  const updatePayment = async (invoice) => {
    const nextStatus = invoice.payment_status === 'paid' ? 'unpaid' : 'paid';
    try {
      const response = await API.put(
        '/api/billing/v8/admin/accounts/' +
          selectedAccount +
          '/monthly-invoices/' +
          invoice.period_start +
          '/payment',
        { payment_status: nextStatus }
      );
      if (!response) return;
      if (!response.data?.success) throw new Error(response.data?.message);
      showSuccess(t('billing.monthly.messages.updated'));
      await Promise.all([loadOrganizations(), loadHistory()]);
    } catch (error) {
      showError(error.message || t('billing.monthly.errors.update'));
    } finally {
    }
  };

  if (loading) return <Loader active inline='centered' />;
  return (
    <section className='billing-monthly-root-v98'>
      <div className='billing-monthly-heading-v98'>
        <div>
          <Header as='h3'>{t('billing.monthly.overview')}</Header>
          <span className='billing-muted-v8'>
            {t('billing.monthly.subtitle')}
          </span>
        </div>
      </div>
      {!organizations.length ? (
        <Message info content={t('billing.monthly.no_organizations')} />
      ) : (
        <div className='management-table-scroll-v72' role='region' tabIndex={0}>
          <Table
            basic='very'
            compact
            unstackable
            className='management-table-v72'
          >
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>
                  {t('billing.monthly.organization')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('billing.monthly.period')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('billing.monthly.amount')}
                </Table.HeaderCell>
                <Table.HeaderCell>{t('billing.monthly.mode')}</Table.HeaderCell>
                <Table.HeaderCell>
                  {t('billing.monthly.payment')}
                </Table.HeaderCell>
                <Table.HeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {organizations.map((organization) => {
                const invoice = organization.current_invoice;
                return (
                  <Table.Row
                    key={organization.account_id}
                    active={String(organization.account_id) === selectedAccount}
                  >
                    <Table.Cell>
                      <strong>{organization.organization_name}</strong>
                    </Table.Cell>
                    <Table.Cell>{formatPeriod(invoice)}</Table.Cell>
                    <Table.Cell>USD {invoice.amount_usd}</Table.Cell>
                    <Table.Cell>
                      {t('billing.mode.' + invoice.payment_mode)}
                    </Table.Cell>
                    <Table.Cell>
                      <InvoiceStatus invoice={invoice} t={t} />
                    </Table.Cell>
                    <Table.Cell>
                      <Button
                        basic
                        size='tiny'
                        icon
                        labelPosition='left'
                        onClick={() =>
                          setSelectedAccount(String(organization.account_id))
                        }
                      >
                        <Icon name='folder open' />
                        {t('billing.monthly.view')}
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        </div>
      )}
      {selectedOrganization ? (
        <MonthlyDataSection
          title={t('billing.monthly.history', {
            organization: selectedOrganization.organization_name,
          })}
          empty={invoices.length ? '' : t('billing.monthly.empty')}
        >
          {historyLoading ? (
            <Loader active inline='centered' />
          ) : invoices.length ? (
            <MonthlyInvoiceTableV98
              invoices={invoices}
              admin
              onPayment={updatePayment}
            />
          ) : null}
        </MonthlyDataSection>
      ) : null}
    </section>
  );
};
